import { fetchQuery, graphql } from 'react-relay';
import { AUTH_CONTENT, AUTH_MODAL } from './authFlowConstants';
import authModalLoader from '../../utils/AuthModalLoader';
import get from 'lodash.get';
import { registrationMutation } from './registration/registrationMutation';
import { authTrackingHandler } from './authTrackingHandler';

export const viewerFragment = graphql`
    fragment contactDealerAuthHandler_viewer on Viewer {
        disableEmailOnlyRegistration: isEnabledByLocale(
            featureName: "DISABLE_EMAIL_ONLY_REGISTRATION"
        )
        disableEmailOnlyMc: isEnabledByLocale(featureName: "DISABLE_EMAIL_ONLY_MC")
    }
`;

const userMetadataQuery = graphql`
    query contactDealerAuthHandlerUserMetadataQuery($email: String = "") {
        viewer {
            userExistsMetadata(email: $email) {
                userExists
                emailOnly
                hasPassword
            }
        }
    }
`;

/**
 * we use the fetchQuery method here
 *    1) because we aren't in a react component context
 *    2) so we can fetch information dynamically for a potentially changing email address
 *    3) doing it in a refetch container would add a lot more code to the ContactDealer.jsx and ContactDealerInlineForm.jsx files (and would mean we couldn't share this code)
 * @param {*} emailField email to check auth metadata for
 * @param {*} environment relay environment
 */
export const fetchUserMetadata = (emailField, environment) => {
    return fetchQuery(environment, userMetadataQuery, {
        email: emailField,
    }).then(data => get(data, 'viewer.userExistsMetadata'));
};

const fireAuthFunction = ({ goToAuthFunction, authType, authOptions, onAuthComplete }) => {
    if (authType === AUTH_CONTENT) {
        // if you are injecting auth content, auth callback is passed as part of auth options
        goToAuthFunction({ ...authOptions, onAuthComplete });
    } else {
        // if you are launching auth modal, auth callback is called from promise resolution
        goToAuthFunction(authOptions).then(onAuthComplete);
    }
};

const shouldShowFullAccountFlow = ({ viewer, flows }) =>
    get(viewer, 'disableEmailOnlyMc') &&
    get(flows, 'auth') !== authModalLoader.constants.CONSULT_AN_EXPERT_FLOW;

// openCreatePassword and openLogin functions need to be exposed as standalone functions so that they can be consumed outside of the contactDealerAuthHandler
export function openCreatePasswordThroughEmailModal({
    goToAuthFunction = authModalLoader.show.bind(authModalLoader),
    emailField,
    environment,
    gaLabel,
}) {
    goToAuthFunction({
        flow: authModalLoader.constants.CREATE_PASSWORD_SEND_MESSAGE_FLOW,
        action: authModalLoader.constants.REQUEST_PASSWORD_EMAIL,
        email: emailField,
        relayEnvironment: environment,
        ga: { label: gaLabel },
    });
}

export function openLoginModal({
    viewer,
    emailField,
    environment,
    gaLabel,
    goToAuthFunction = authModalLoader.show.bind(authModalLoader),
    authType = AUTH_MODAL,
    onAuthComplete,
    flows,
}) {
    const showFullAccountFlow = shouldShowFullAccountFlow({ viewer, flows });
    const authOptions = {
        flow: showFullAccountFlow
            ? get(flows, 'fullAccount') ||
              authModalLoader.constants.FULL_ACCOUNT_CONTACT_DEALER_FLOW
            : get(flows, 'auth') || authModalLoader.constants.CONTACT_DEALER_FLOW,
        action: authModalLoader.constants.LOGIN,
        email: emailField,
        relayEnvironment: environment,
        ga: { label: gaLabel },
        shouldPauseAuthFlow: showFullAccountFlow ? undefined : ({ step }) => step === 0,
    };
    fireAuthFunction({ goToAuthFunction, authType, authOptions, onAuthComplete });
}

/**
 *
 * @param {String} gaLabel string to pass into ga label
 * @param {String: "AUTH_MODAL" | "AUTH_CONTENT"} authType string denoting how auth content is being launched
 * @param {Function} goToAuthFunction function for launching/switching to auth
 * @param {Function} startLoadingUI function for starting loading UI for consuming component
 * @param {Function} handleErrors function to handle errors
 * @param {String} emailField email being registered
 * @param {Boolean} marketingOptIn has the user opted in to marketing campaign
 * @param {Object} environment relay environment
 * @param {Function} sendMessage  function which sends the message
 * @param {Function} updateProfileBeforeSubmitting function which updates the profile then submits the message
 * @param {Object} viewer the damn viewer (from relay)
 * @param {Function} closeModal function to close the modal once your done (AUTH_CONTENT only)
 */
export function contactDealerAuthHandler({
    gaLabel,
    authType = AUTH_MODAL,
    goToAuthFunction = authModalLoader.show.bind(authModalLoader),
    startLoadingUI,
    handleErrors,
    emailField,
    marketingOptIn,
    environment,
    sendMessage,
    updateProfileBeforeSubmitting,
    viewer,
    closeModal = () => {},
    flows,
}) {
    const disableEmailOnlyRegistration = get(viewer, 'disableEmailOnlyRegistration');
    const showFullAccountFlow = shouldShowFullAccountFlow({ viewer, flows });

    function openNeedsFullAccountModal(userRegistration) {
        const authOptions = {
            flow:
                get(flows, 'fullAccount') ||
                authModalLoader.constants.PASSWORD_ONLY_FULL_ACCOUNT_CONTACT_DEALER_FLOW,
            action: authModalLoader.constants.REGISTER,
            email: emailField,
            authResponse: { accountCreated: true, ...userRegistration },
            relayEnvironment: environment,
            ga: { label: gaLabel },
        };
        const onAuthComplete = res => {
            sendMessage({
                userId: res.userId,
                user: res.user,
                fromAuthCallback: true,
            });
        };
        fireAuthFunction({ goToAuthFunction, authType, authOptions, onAuthComplete });
    }

    function openCompleteAccountModal(userRegistration) {
        goToAuthFunction({
            flow:
                get(flows, 'completeAccount') ||
                authModalLoader.constants.PASSWORD_ONLY_CONTACT_DEALER_FLOW,
            action: authModalLoader.constants.REGISTER,
            email: emailField,
            authResponse: { accountCreated: true, ...userRegistration },
            relayEnvironment: environment,
            ga: { label: gaLabel },
            onAuthComplete: closeModal,
        });
    }

    function fireSendFunction(res, onSuccess) {
        const payload = {
            onSuccess,
            userId: res.userId,
            user: res.user,
            fromAuthCallback: true,
        };
        if (res.isNewUser) {
            updateProfileBeforeSubmitting(payload);
        } else {
            sendMessage(payload);
        }
    }

    function registerUserAndSendMessage() {
        startLoadingUI();
        registrationMutation(environment, {
            email: emailField,
            marketingOptIn,
        })
            .then(({ userRegistration }) => {
                const ga = { label: gaLabel };
                authTrackingHandler({ userRegistration, environment: environment, ga });

                // if email only messaging is disabled, we want to prompt account completion
                // if account is completed, send the message
                if (showFullAccountFlow) {
                    openNeedsFullAccountModal(userRegistration);
                } else {
                    // otherwise, send the message and prompt user to complete account afterwords
                    fireSendFunction(userRegistration, () =>
                        openCompleteAccountModal(userRegistration)
                    );
                }
            })
            .catch(err => {
                handleErrors(get(err, 'source.errors.0.message'));
            });
    }

    // MAIN
    return fetchUserMetadata(emailField, environment).then(userExistsMetadata => {
        // if the user doesn't exist yet and email only registration is enabled, create an account for the email address entered
        if (!get(userExistsMetadata, 'userExists') && !disableEmailOnlyRegistration) {
            registerUserAndSendMessage();
        } else if (get(userExistsMetadata, 'emailOnly') && !disableEmailOnlyRegistration) {
            // if you try to register as an existing email only account, prompt user to create new password via email
            openCreatePasswordThroughEmailModal({
                goToAuthFunction,
                emailField,
                environment,
                gaLabel,
            });
        } else {
            // otherwise, you are trying to register as an existing full account
            // in this case, prompt user to login and send message once they have
            openLoginModal({
                viewer,
                emailField,
                environment,
                gaLabel,
                goToAuthFunction,
                authType,
                onAuthComplete: fireSendFunction,
                flows,
            });
        }
    });
}
